import { PreviewRowData, ColumnDefinition } from './types'

/**
 * Calculate dynamic column widths using content-based sizing for fixed columns
 * and flexible distribution for variable-width columns.
 */
export const calculateColumnWidths = (
  columns: ColumnDefinition[],
  rows: PreviewRowData[]
): number[] => {
  const totalWidth = 100

  const fixedWidthColumns = new Set([
    'serial', 'year', 'aftt', 'emp', 'damage',
    'dateListed', 'dates', 'dateRemoved',
    'askingTake', 'prices',
    'custom1', 'custom2', 'custom3'
  ])

  const flexibleColumns = new Set(['owner', 'broker', 'options', 'relativeValue'])

  const charWidthPct = 0.6

  const columnPadding: Record<string, number> = {
    'serial': 1.5,
    'year': 1.0,
    'aftt': 1.0,
    'emp': 0.8,
    'damage': 1.5,
    'dateListed': 0.4,
    'dates': 0.4,
    'dateRemoved': 0.4,
    'askingTake': 1.0,
    'prices': 1.5,
    'custom1': 0.6,
    'custom2': 0.6,
    'custom3': 0.6
  }

  const measureByWord = new Set(['emp'])

  const measureColumnContent = (dataKey: string, header: string): number => {
    const headerLines = header.split('\n')
    let maxLength = Math.max(...headerLines.map(line => line.length))

    rows.forEach(row => {
      const value = row[dataKey]
      if (typeof value === 'string') {
        const lines = value.split('\n')

        if (measureByWord.has(dataKey)) {
          lines.forEach(line => {
            const words = line.split(/\s+/)
            words.forEach(word => {
              maxLength = Math.max(maxLength, word.length)
            })
          })
        } else {
          const longestLine = Math.max(...lines.map(line => line.length))
          maxLength = Math.max(maxLength, longestLine)
        }
      }
    })

    return maxLength
  }

  const fixedWidths: Map<string, number> = new Map()
  let totalFixedWidth = 0

  columns.forEach(col => {
    if (fixedWidthColumns.has(col.dataKey)) {
      const contentLength = measureColumnContent(col.dataKey, col.header)
      const padding = columnPadding[col.dataKey] ?? 1.0
      const calculatedWidth = (contentLength * charWidthPct) + padding

      fixedWidths.set(col.dataKey, calculatedWidth)
      totalFixedWidth += calculatedWidth
    }
  })

  const remainingWidth = Math.max(totalWidth - totalFixedWidth, 20)

  const enabledFlexColumns = columns.filter(col => flexibleColumns.has(col.dataKey))
  const hasOwner = enabledFlexColumns.some(col => col.dataKey === 'owner')
  const hasBroker = enabledFlexColumns.some(col => col.dataKey === 'broker')
  const hasOptions = enabledFlexColumns.some(col => col.dataKey === 'options')
  const hasRelativeValue = enabledFlexColumns.some(col => col.dataKey === 'relativeValue')

  let ownerBrokerWidth = 0
  let optionsRelValueWidth = 0

  const pair1Count = (hasOwner ? 1 : 0) + (hasBroker ? 1 : 0)
  const pair2Count = (hasOptions ? 1 : 0) + (hasRelativeValue ? 1 : 0)

  if (pair1Count > 0 || pair2Count > 0) {
    const totalUnits = pair1Count * 1 + pair2Count * 1.5
    const unitWidth = remainingWidth / totalUnits

    ownerBrokerWidth = unitWidth * 1
    optionsRelValueWidth = unitWidth * 1.5
  }

  const finalWidths = columns.map(col => {
    if (fixedWidths.has(col.dataKey)) {
      return fixedWidths.get(col.dataKey)!
    }
    if (col.dataKey === 'owner' || col.dataKey === 'broker') {
      return ownerBrokerWidth
    }
    if (col.dataKey === 'options' || col.dataKey === 'relativeValue') {
      return optionsRelValueWidth
    }
    return 5
  })

  const currentTotal = finalWidths.reduce((sum, w) => sum + w, 0)
  return finalWidths.map(w => (w / currentTotal) * 100)
}

